import { IconButton, Menu, toast } from '@neo4j-ndl/react';
import { EllipsisHorizontalIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes, fleetManagementApi, useActiveProjectSummary } from '@nx/state';
import { isNonEmptyString, isNullish } from '@nx/stdlib';
import { DangerConfirmModal } from '@nx/ui';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { usePermissions } from '../hooks/use-permissions';
import { ConnectDeploymentWizardModal } from '../modals/create-deplyment-modals/connect-deployment-wizard-modal';
import { RenameDeploymentModal } from '../modals/rename-deployment-modal';
import { UpdateConnectionUrlModal } from '../modals/update-connection-url-modal';

type DeploymentActionModalsProps = {
  deployment: FleetManagementTypes.DeploymentDetails;
  isRenameModalOpen: boolean;
  setIsRenameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteModalOpen: boolean;
  setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCreateTokenModalOpen: boolean;
  setIsCreateTokenModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isUpdateConnectionUrlModalOpen: boolean;
  setIsUpdateConnectionUrlModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DeploymentActionModals = ({
  deployment,
  isRenameModalOpen,
  setIsRenameModalOpen,
  isDeleteModalOpen,
  setIsDeleteModalOpen,
  isCreateTokenModalOpen,
  setIsCreateTokenModalOpen,
  isUpdateConnectionUrlModalOpen,
  setIsUpdateConnectionUrlModalOpen,
}: DeploymentActionModalsProps) => {
  const activeProjectSummary = useActiveProjectSummary();

  const [deleteDeployment] = fleetManagementApi.useDeleteDeploymentMutation();
  const [deleteDeploymentLoading, setDeleteDeploymentLoading] = useState(false);

  return (
    <>
      <DangerConfirmModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Unregister deployment"
        content="Are you sure you want to unregister this deployment?"
        confirmButtonText="Unregister"
        onConfirm={() => {
          setDeleteDeploymentLoading(true);
          deleteDeployment({ projectId: activeProjectSummary?.id ?? '', deploymentId: deployment.deploymentId })
            .then(({ error }) => {
              if (error) {
                toast.danger('Failed to delete deployment', { shouldAutoClose: true });
              } else {
                toast.success(`Deployment deleted`, { shouldAutoClose: true });
              }
            })
            .catch((err: Error) => {
              toast.danger(err.message, { shouldAutoClose: true });
            })
            .finally(() => {
              setIsDeleteModalOpen(false);
              setDeleteDeploymentLoading(false);
            });
        }}
        loading={deleteDeploymentLoading}
        error={null}
      />
      <RenameDeploymentModal
        isOpen={isRenameModalOpen}
        closeModal={() => setIsRenameModalOpen(false)}
        deploymentId={deployment.deploymentId}
        currentName={deployment.name}
      />
      <ConnectDeploymentWizardModal
        isOpen={isCreateTokenModalOpen}
        closeModal={() => setIsCreateTokenModalOpen(false)}
        deploymentId={deployment.deploymentId}
      />
      <UpdateConnectionUrlModal
        isOpen={isUpdateConnectionUrlModalOpen}
        closeModal={() => setIsUpdateConnectionUrlModalOpen(false)}
        deploymentId={deployment.deploymentId}
        currentConnectionUrl={deployment.connectionUrl}
      />
    </>
  );
};

type DeploymentTableRowActionProps = {
  deployment: FleetManagementTypes.DeploymentDetails;
  type?: 'table' | 'list';
  handleDisplayDbmsDetails: (deployment: FleetManagementTypes.DeploymentDetails) => void;
};

export const DeploymentActionMenu = ({
  deployment,
  handleDisplayDbmsDetails,
  type = 'list',
}: DeploymentTableRowActionProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateTokenModalOpen, setIsCreateTokenModalOpen] = useState(false);
  const [isUpdateConnectionUrlModalOpen, setIsUpdateConnectionUrlModalOpen] = useState(false);

  const isDeploymentUpdateAllowed = usePermissions().isAllowed('deployments', 'write');
  const isTokenUpdateAllowed = usePermissions().isAllowed('tokens', 'write');

  const menuRef = useRef<HTMLButtonElement>(null);

  const onDetails = () => {
    void navigate(`${deployment.deploymentId}/details/`);
  };

  return (
    <>
      <IconButton
        ariaLabel="Deployment actions"
        onClick={() => setIsOpen(!isOpen)}
        ref={menuRef}
        size={type === 'table' ? 'small' : 'medium'}
      >
        <EllipsisHorizontalIconOutline className="h-full w-full" />
      </IconButton>
      <Menu isOpen={isOpen} anchorRef={menuRef} onClose={() => setIsOpen(false)}>
        <Menu.Items>
          {isNullish(deployment.token) && (
            <Menu.Item
              title="Monitor deployment"
              isDisabled={!isTokenUpdateAllowed}
              onClick={() => {
                setIsCreateTokenModalOpen(true);
                setIsOpen(false);
              }}
            />
          )}
          <Menu.Item
            title="Inspect"
            onClick={() => {
              handleDisplayDbmsDetails(deployment);
              setIsOpen(false);
            }}
          />
          <Menu.Item
            title="Show details"
            isDisabled={isNullish(deployment.dbms)}
            onClick={() => {
              onDetails();
              setIsOpen(false);
            }}
          />
          <Menu.Item
            title={isNonEmptyString(deployment.connectionUrl) ? 'Update connection URL' : 'Add connection URL'}
            isDisabled={!isDeploymentUpdateAllowed}
            onClick={() => {
              setIsUpdateConnectionUrlModalOpen(true);
              setIsOpen(false);
            }}
          />
          <Menu.Item
            title="Rename"
            isDisabled={!isDeploymentUpdateAllowed}
            onClick={() => {
              setIsRenameModalOpen(true);
              setIsOpen(false);
            }}
          />
          <Menu.Divider />

          <Menu.Item
            title="Unregister deployment"
            isDisabled={!isDeploymentUpdateAllowed}
            className="text-danger-text"
            onClick={() => setIsDeleteModalOpen(true)}
          />
        </Menu.Items>
      </Menu>
      <DeploymentActionModals
        deployment={deployment}
        isRenameModalOpen={isRenameModalOpen}
        setIsRenameModalOpen={setIsRenameModalOpen}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        isCreateTokenModalOpen={isCreateTokenModalOpen}
        setIsCreateTokenModalOpen={setIsCreateTokenModalOpen}
        isUpdateConnectionUrlModalOpen={isUpdateConnectionUrlModalOpen}
        setIsUpdateConnectionUrlModalOpen={setIsUpdateConnectionUrlModalOpen}
      />
    </>
  );
};
